import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRoomPort, requestBone, requestTreat, requestTV, getTVStatus, getUnreadCount, getCommonCameras, getRelayStatus, requestRelay } from '../services/api';
import { AppConstants } from '../AppConstants';

import 'bootstrap-icons/font/bootstrap-icons.css';
import ConfirmBox from '../components/ConfirmBox';
import AlertBox from '../components/AlertBox';
import RelayDialog from '../components/RelayDialog';
import TVDialog from '../components/TVDialog';

import OneSignal from 'react-onesignal';

const VideoStream = () => {
    const [streams, setStreams] = useState([]);
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [currentStreamIndex, setCurrentStreamIndex] = useState(0);
    const [dispPort, setDispenserPort] = useState(0);
    const [loading, setLoading] = useState(true);
    const [chatUnreadCount, setChatUnreadCount] = useState(0);
    const [tvOn, setTvOn] = useState(false);
    const [tvDialogOpen, setTvDialogOpen] = useState(false);
    const [tvHours, setTvHours] = useState(1);
    const [confirmAction, setConfirmAction] = useState(null);
    const [alertMessage , setAlertMessage] = useState(null);
    const [dispenserCamSrc, setDispenserCamSrc] = useState("");
    const [roomCamSrc, setRoomCamSrc] = useState("");
    const [relayDialogOpen, setRelayDialogOpen] = useState(false);
    const [relayOn, setRelayOn] = useState(false);
    const [relayHours, setRelayHours] = useState(1);
    const carouselRef = useRef(null);
    const switchBackTimeoutRef = useRef(null);

    // FIX: Initial Setup
    useEffect(() => {
        const token = localStorage.getItem('token');
        const userData = localStorage.getItem('user');
        if (!token) {
            navigate('/login');
            return;
        }
        if (userData) {
            const parsedUser = JSON.parse(userData);
            setUser(parsedUser);
            const roomId = parsedUser.rooms === '*' ? AppConstants.DEFAULT_ROOM_ID : parsedUser.rooms?.split(',')[0];
            if (roomId) {
                fetchCameras(roomId);
                fetchTVStatus(roomId);
                const interval = setInterval(() => fetchTVStatus(roomId), 30000);
                return () => clearInterval(interval);
            }
        }
    }, [navigate]);

    const setChatLastRead = (dt) => localStorage.setItem("chat_last_read", dt.toJSON());
    const getChatLastRead = () => localStorage.getItem("chat_last_read") || "1970-01-01T00:00:00";

    const fetchTVStatus = async (roomId) => {
        try {
            const response = await getTVStatus(roomId);
            if (response.is_on !== undefined) setTvOn(response.is_on);
        } catch (err) { console.error("Error fetching TV status:", err); }
    };

    // FIX: Chat Unread Count Polling
    useEffect(() => {
        if (!user) return;
        const fetchUnreadCount = async () => {
            const roomId = user?.rooms === '*' ? AppConstants.DEFAULT_ROOM_ID : user?.rooms?.split(',')[0];
            try {
                const response = await getUnreadCount(roomId, getChatLastRead());
                if (response.count !== undefined) {
                    setChatUnreadCount(response.count);
                }
            } catch (err) { console.error("Error fetching unread count:", err); }
        };
        fetchUnreadCount();
        const interval = setInterval(fetchUnreadCount, 5000);
        return () => clearInterval(interval);
    }, [user]);

    // FIX: Camera Feed Source Update (Corrected syntax and intervals)
    useEffect(() => {
        if (!user) return;
        const interval = setInterval(async () => {
            const roomId = user?.rooms === '*' ? AppConstants.DEFAULT_ROOM_ID : user?.rooms?.split(',')[0];
            
            if (dispPort <= 0) {
                const dPort = await getRoomPort(roomId);
                setDispenserPort(dPort);
            } else {
                const noCache = new Date().getTime(); 
                setDispenserCamSrc(`${AppConstants.DISPENSER_URL_ROOT}/${dispPort}/api/v1/dispenser_cam/video_feed?t=${noCache}`);
                setRoomCamSrc(`${AppConstants.DISPENSER_URL_ROOT}/${dispPort}/api/v1/room_cam/video_feed?t=${noCache}`);
            }
        }, 300); 

        return () => clearInterval(interval);
    }, [user, dispPort]);

    const handleBoneClick = () => setConfirmAction('bone');
    const handleTreatClick = () => setConfirmAction('treat');

    const handleConfirmAction = async () => {
        const roomId = user?.rooms === '*' ? AppConstants.DEFAULT_ROOM_ID : user?.rooms?.split(',')[0];
        if (!roomId) return;
        try {
            if (confirmAction === 'bone') {
                await requestBone(roomId);
                setAlertMessage("Bone request submitted");
                switchToRPICamera();
            } else if (confirmAction === 'treat') {
                await requestTreat(roomId);
                setAlertMessage("Treat request submitted");
                switchToRPICamera();
            }
            setConfirmAction(null);
        } catch (err) {
            setAlertMessage(err.message);
            setConfirmAction(null);
        }
    };

    const handleTVClick = () => {
        setTvHours(1);
        setTvDialogOpen(true);
    };

    const handleConfirmTV = async () => {
        const roomId = user?.rooms === '*' ? AppConstants.DEFAULT_ROOM_ID : user?.rooms?.split(',')[0];
        if (!roomId) return;
        try {
            const action = tvOn ? 'off' : 'on';
            const response = await requestTV(roomId, action, tvOn ? undefined : tvHours * 60);
            if (response?.message) {
                setAlertMessage(response.message);
            }
            fetchTVStatus(roomId);
            setTvDialogOpen(false);
        } catch (err) {
            console.error("Error:", err);
            setAlertMessage(err.message || 'Failed to switch TV');
            setTvDialogOpen(false);
        }
    };

    const handleRelayClick = async () => {
        const roomId = user?.rooms === '*' ? AppConstants.DEFAULT_ROOM_ID : user?.rooms?.split(',')[0];
        if (!roomId) return;
        try {
            const response = await getRelayStatus(roomId);
            console.log("Relay status response:", response);
            setRelayOn(response?.is_on === true);
            setRelayHours(1);
            setRelayDialogOpen(true);
        } catch (err) {
            console.error("Error fetching relay status:", err);
            setAlertMessage(err.message || 'Failed to fetch relay status');
        }
    };

    const handleConfirmRelay = async () => {
        const roomId = user?.rooms === '*' ? AppConstants.DEFAULT_ROOM_ID : user?.rooms?.split(',')[0];
        if (!roomId) return;
        try {
            const action = relayOn ? 'off' : 'on';
            const response = await requestRelay(roomId, action, relayOn ? undefined : relayHours * 60);
            console.log("Relay request response:", response);
            if (response?.message) {
                setAlertMessage(response.message);
            }
            setRelayDialogOpen(false);
        } catch (err) {
            console.error("Error:", err);
            setAlertMessage(err.message || 'Failed to switch relay');
            setRelayDialogOpen(false);
        }
    };

    const getConfirmActionMessage = () => {
        if(confirmAction == null) return "Unknown";
        if(confirmAction === 'bone' ) return 'Dispense a Milk Bone?' ;
        if(confirmAction === 'treat') return 'Dispense a Treat?' ;
        return "Unknown";
    };

    const getConfirmActionImage = () => {
        if(confirmAction === 'bone') return '/milk_bone.jpg';
        if(confirmAction === 'treat') return '/mini_treat.jpg';
        return null;
    };

    const fetchCameras = async (roomId) => {
        const fallbackStreams = [
            { id: 1, type: 'ipcam', url: 'https://ipcamlive.com/player/player.php?alias=demo1', name: 'Camera 1' },
            { id: 2, type: 'ipcam', url: 'https://ipcamlive.com/player/player.php?alias=demo2', name: 'Camera 2' }
        ];
        try {
            setLoading(true);
            const response = await getCommonCameras(roomId);
            const cameraUrls = response["*"];
            let cameraStreams = [];

            if (cameraUrls && Object.keys(cameraUrls).length > 0) {
                var camCount=1;
                cameraStreams.push({ id: camCount++, type: 'room', name: `Camera ${camCount}` });
                Object.keys(cameraUrls).forEach((key, idx) => {
                    if (key.startsWith("camera_") && cameraUrls[key] !== "0.00") {
                        cameraStreams.push({
                            id: camCount,
                            type: 'ipcam',
                            url: cameraUrls[key],
                            name: `Camera ${camCount}`
                        });
                        camCount++;
                    }
                });
                cameraStreams.push({ id: camCount++, type: 'dispenser', name: `Camera ${camCount}` });
                setStreams(cameraStreams.slice(0, 7));
            } else {
                setStreams(fallbackStreams);
            }
        } catch (err) {
            setStreams(fallbackStreams);
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        OneSignal.logout();
        navigate('/login');
    };

    const switchToCamera = (index) => {
        if (carouselRef.current) {
            const item = carouselRef.current.children[index];
            if (item) {
                item.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                setCurrentStreamIndex(index);
            }
        }
    };

    const switchToRPICamera = () => {
        // Clear any existing timeout
        if (switchBackTimeoutRef.current) {
            clearTimeout(switchBackTimeoutRef.current);
            switchBackTimeoutRef.current = null;
        }
        
        const rpiIndex = streams.findIndex(s => s.type === 'dispenser');
        if (rpiIndex !== -1) {
            switchToCamera(rpiIndex);
            
            // Wait for 30sec then switch back to first camera (index 0)
            switchBackTimeoutRef.current = setTimeout(() => {
                switchToCamera(0);
                switchBackTimeoutRef.current = null;
            }, 30000); 
        }
    };

    useEffect(() => {
        const carousel = carouselRef.current;
        if (!carousel) return;
        const handleScroll = () => {
            const index = Math.round(carousel.scrollLeft / carousel.clientWidth);
            setCurrentStreamIndex(index);
        };
        carousel.addEventListener('scroll', handleScroll);
        return () => carousel.removeEventListener('scroll', handleScroll);
    }, [streams]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (switchBackTimeoutRef.current) {
                clearTimeout(switchBackTimeoutRef.current);
            }
        };
    }, []);

    if (loading) return <div className="flex-center" style={{ minHeight: '100vh', background: '#ffffff' }}>Loading cameras...</div>;

    return (
        <div style={{ width: '100%', maxWidth: '480px', margin: '0 auto', height: '100vh', display: 'flex', flexDirection: 'column', background: '#ffffff', position: 'relative' }}>
            
            {/* Restored Header UI */}
            <header style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#ffffff', borderBottom: '1px solid #e0e0e0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <img src="/dog_mascot.png" alt="Logo" style={{ height: '32px', width: 'auto', objectFit: 'contain' }} />
                    <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: '#000000', fontFamily: 'Arial, sans-serif' }}>My Pup</h2>
                </div>
                <button onClick={handleLogout} style={{ background: 'transparent', border: 'none', color: '#AE3041', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px' }} aria-label="Logout">
                    <i className="bi bi-box-arrow-right" style={{ fontSize: '24px' }}></i>
                </button>
            </header>

            {/* Video Carousel */}
            <div className="carousel-container" ref={carouselRef} style={{ flex: 0.8, marginTop: 0, background: '#000' }}>

            
            {streams.map((stream, index) => {
                // Only load the stream if it's the current slide
                const isActive = index === currentStreamIndex;
                
                // Determine the base URL
                const baseUrl = stream.type === 'dispenser' 
                    ? dispenserCamSrc 
                    : (stream.type === 'room' ? roomCamSrc : stream.url);

                return (
                    <div key={stream.id} className="carousel-item">
                        <div style={{ width: '100%', height: '100%', background: '#000', position: 'relative' }}>
                            {isActive ? (
                                <>
                                    {/* CHOICE: Use IMG for local Pi, IFRAME for cloud services */}
                                    {(stream.type === 'dispenser' || stream.type === 'room') ? (
                                        <img 
                                            src={`${baseUrl}` || null} 
                                            style={{ width: '100%', height: '100%', objectFit: 'contain' }} 
                                            alt="Local Feed" 
                                            />
                                    ) : (
                                        <iframe
                                            src={`${baseUrl}` }
                                            title={`External Stream ${stream.id}`}
                                            style={{ width: '100%', height: '100%', border: 'none' }}
                                            allow="autoplay; encrypted-media"
                                            allowFullScreen
                                            />
                                    )}
                                </>
                            ) : (
                                <div className="placeholder" style={{ color: '#555', textAlign: 'center', paddingTop: '20%' }}>
                                    Stream Paused (Slide to Resume)
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
            </div>
            {/*
            <div className="carousel-container" ref={carouselRef} style={{ flex: 1, marginTop: 0, background: '#000' }}>
                {streams.map((stream, index) => (
                    <div key={stream.id} className="carousel-item">
                        <div style={{ width: '100%', height: '100%', background: '#000', position: 'relative' }}>
                            <iframe
                                src={stream.type === 'dispenser' ? dispenserCamSrc : (stream.type === 'room' ? roomCamSrc : stream.url)}
                                title={`Camera ${stream.id}`}
                                style={{ width: '100%', height: '100%', border: 'none' }}
                                allow="autoplay; encrypted-media"
                                allowFullScreen
                            />
                        </div>
                    </div>
                ))}
            </div>
            */}
            {/* Pagination Dots */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', padding: '12px', background: '#ffffff' }}>
                {streams.map((stream, index) => (
                    <div 
                        key={stream.id} 
                        onClick={() => switchToCamera(index)}
                        style={{ cursor: 'pointer', width: index === currentStreamIndex ? '10px' : '8px', height: index === currentStreamIndex ? '10px' : '8px', backgroundColor: index === currentStreamIndex ? '#666666' : '#d0d0d0', borderRadius: '50%', transition: 'all 0.2s ease' }}
                    />
                ))}
            </div>

            {/* Icon Grid - Restored original SVG and button styles */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', padding: '16px', background: '#ffffff' }}>
                {/* Audio */}
                <button onClick={() => navigate('/audio')} style={{ aspectRatio: '1', background: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#AE3041' }} aria-label="Record Audio">
                    <i className="bi bi-mic-fill" style={{ fontSize: '32px' }}></i>
                </button>
                {/* Bone */}
                <button onClick={handleBoneClick} style={{ aspectRatio: '1', background: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#AE3041' }} aria-label="Give Bone">
                    <svg width="32" height="32" xmlns="www.w3.org" viewBox="0 0 32 32">
                        <rect x="6" y="12" width="20" height="8" fill="#AE3041" stroke="#AE3041" strokeWidth="0.5"/>
                        <circle cx="6" cy="12" r="4" fill="#AE3041" stroke="#AE3041" strokeWidth="0.5"/><circle cx="6" cy="20" r="4" fill="#AE3041" stroke="#AE3041" strokeWidth="0.5"/>
                        <circle cx="26" cy="12" r="4" fill="#AE3041" stroke="#AE3041" strokeWidth="0.5"/><circle cx="26" cy="20" r="4" fill="#AE3041" stroke="#AE3041" strokeWidth="0.5"/>
                    </svg>
                </button>
                {/* Treat */}
                <button onClick={handleTreatClick} style={{ aspectRatio: '1', background: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#AE3041' }} aria-label="Give Treat">
                    <svg width="32" height="32" xmlns="www.w3.org" viewBox="0 0 32 32">
                        <rect x="8" y="12" width="16" height="8" fill="#AE3041" rx="3" ry="3"/>
                        <polygon points="8,12 2,10 2,22 8,20" fill="#AE3041" stroke="#AE3041" strokeWidth="0.5"/>
                        <polygon points="24,12 30,10 30,22 24,20" fill="#AE3041" stroke="#AE3041" strokeWidth="0.5"/>
                    </svg>
                </button>
                {/* TV */}
                <button onClick={handleTVClick} style={{ aspectRatio: '1', background: tvOn ? '#4CAF50' : '#f5f5f5', border: tvOn ? '1px solid #4CAF50' : '1px solid #e0e0e0', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', position: 'relative', color: tvOn ? '#ffffff' : '#AE3041' }} aria-label="TV">
                    <i className="bi bi-tv-fill" style={{ fontSize: '32px' }}></i>
                    {tvOn && <span style={{ position: 'absolute', top: '4px', right: '4px', color: '#4CAF50', fontSize: '14px', background: '#ffffff', borderRadius: '50%', width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>✓</span>}
                </button>
                {/* Shopping */}
                <button onClick={() => navigate('/shopping')} style={{ aspectRatio: '1', background: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#AE3041' }}>
                    <i className="bi bi-cart-fill" style={{ fontSize: '32px' }}></i>
                </button>
                {/* Preferences */}
                <button onClick={() => navigate('/preferences')} style={{ aspectRatio: '1', background: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#AE3041' }}>
                    <svg width="32" height="32" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
                        <line x1="4" y1="8" x2="28" y2="8" fill="none" stroke="#AE3041" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="22" cy="8" r="3" fill="#AE3041" stroke="#AE3041" strokeWidth="1.5"/>
                        <line x1="4" y1="16" x2="28" y2="16" fill="none" stroke="#AE3041" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="12" cy="16" r="3" fill="#AE3041" stroke="#AE3041" strokeWidth="1.5"/>
                        <line x1="4" y1="24" x2="28" y2="24" fill="none" stroke="#AE3041" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="20" cy="24" r="3" fill="#AE3041" stroke="#AE3041" strokeWidth="1.5"/>
                    </svg>
                </button>
            </div>

            {/* Chat Button */}
            <div style={{ display: 'flex', justifyContent: 'center', padding: '16px', background: '#ffffff' }}>
                <button onClick={ () => {
                        setChatLastRead(new Date());
                        navigate('/chat');
                    }} style={{ padding: '12px 32px', background: '#AE3041', color: '#ffffff', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', minWidth: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    Chat {chatUnreadCount > 0 && `(${chatUnreadCount} new messages)`}
                </button>
            </div>
            {/*
            Relay Button 
            <div style={{ display: 'flex', justifyContent: 'center', padding: '16px', background: '#ffffff' }}>
                <button onClick={handleRelayClick} style={{ padding: '12px 32px', background: '#AE3041', color: '#ffffff', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', minWidth: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    Relay
                </button>
            </div>
            */}
            {/* Modals */}
            <ConfirmBox
                isOpen={confirmAction !=null}
                message={getConfirmActionMessage()}
                image={getConfirmActionImage()}
                onCancel={() => setConfirmAction(null)}
                onConfirm={handleConfirmAction}
            />
            <AlertBox
                show = {alertMessage != null}
                title="Alert"
                message={alertMessage}
                onOK= {()=>setAlertMessage(null)}/>

            <RelayDialog
                isOpen={relayDialogOpen}
                relayOn={relayOn}
                hours={relayHours}
                onHoursChange={setRelayHours}
                onConfirm={handleConfirmRelay}
                onCancel={() => setRelayDialogOpen(false)}
            />

            <TVDialog
                isOpen={tvDialogOpen}
                tvOn={tvOn}
                hours={tvHours}
                onHoursChange={setTvHours}
                onConfirm={handleConfirmTV}
                onCancel={() => setTvDialogOpen(false)}
            />

            {/* confirmAction && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: '#ffffff', padding: '24px', borderRadius: '8px', width: '90%', maxWidth: '300px', textAlign: 'center' }}>
                        <p style={{ marginBottom: '16px', fontSize: '16px', color: '#000' }}>
                            {confirmAction === 'bone' ? 'Dispense a Milk Bone?' : 
                             confirmAction === 'treat' ? 'Dispense a Treat?' : 
                             confirmAction === 'tv' ? `TV will switch ${tvOn ? 'Off' : 'On for 1 hr'}. Are you sure to continue?` : ''}
                        </p>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button onClick={handleConfirmAction} style={{ flex: 1, padding: '12px', background: '#AE3041', color: '#ffffff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>Confirm</button>
                            <button onClick={() => setConfirmAction(null)} style={{ flex: 1, padding: '12px', background: '#f5f5f5', color: '#000', border: '1px solid #e0e0e0', borderRadius: '8px', cursor: 'pointer' }}>Cancel</button>
                        </div>
                    </div>
                </div>
            )*/}
        </div>
    );
};

export default VideoStream;
